import styled, { keyframes } from 'styled-components'
import { tokens } from 'styling/tokens'

const flyoutIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

export const SidebarRoot = styled.aside<{ $collapsed?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  width: ${(props) => (props.$collapsed ? '4rem' : '14rem')};
  padding: ${(props) => (props.$collapsed ? `${tokens.space.md} 0.5rem` : tokens.space.md)};
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.sm};
  transition: width 0.2s ease, padding 0.2s ease;

  @media (max-width: 1024px) {
    display: none;
  }
`

export const Brand = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
  flex-shrink: 0;
  width: 100%;
  padding: ${tokens.space.xs};
  margin-bottom: ${tokens.space.sm};
`

export const NavScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`

export const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

export const NavModule = styled.div`
  display: flex;
  flex-direction: column;
`

export const SubNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin: 0.375rem 0 0.25rem 1.375rem;
  padding-left: 0.625rem;
  border-left: 1px solid ${tokens.border.hairline};
`

export const SubNavItem = styled.button<{ $active?: boolean }>`
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.375rem 0.5rem;
  border-radius: 8px;
  font-size: ${tokens.font.label};
  color: ${(props) => (props.$active ? tokens.accent.onDark : tokens.text.muted)};
  font-weight: ${(props) => (props.$active ? '500' : '400')};
  cursor: pointer;
  transition: color ${tokens.motion.duration} ${tokens.motion.ease},
    background ${tokens.motion.duration} ${tokens.motion.ease};

  &:hover {
    color: ${tokens.text.primary};
    background: ${tokens.surface.inset};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.accent.base};
    outline-offset: 2px;
  }
`

export const SubNavCollapse = styled.div<{ $open?: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.26s ${tokens.motion.ease};
`

export const SubNavInner = styled.div<{ $open?: boolean }>`
  overflow: hidden;
  min-height: 0;
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transition: opacity 0.2s ${tokens.motion.ease};
`

export const Flyout = styled.div`
  position: fixed;
  z-index: 200;
  min-width: 11rem;
  max-width: 15rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: ${tokens.space.sm};
  background: ${tokens.surface.raised};
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.card};
  box-shadow: ${tokens.shadow.card};
  backdrop-filter: blur(${tokens.blur});
  overflow-y: auto;
  animation: ${flyoutIn} ${tokens.motion.duration} ${tokens.motion.ease};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -0.625rem;
    width: 0.625rem;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.border.strong};
    border-radius: 3px;
  }
`

export const FlyoutTitle = styled.div`
  font-size: ${tokens.font.micro};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${tokens.text.muted};
  padding: 0.25rem 0.5rem 0.375rem;
`

export const CollapseToggle = styled.button<{ $collapsed?: boolean }>`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
  gap: ${tokens.space.sm};
  height: 2.5rem;
  width: ${(props) => (props.$collapsed ? '2.75rem' : '100%')};
  padding: ${(props) => (props.$collapsed ? '0' : `0 ${tokens.space.sm}`)};
  border: none;
  background: transparent;
  border-radius: ${tokens.radius.nav};
  color: ${tokens.text.muted};
  font-size: ${tokens.font.body};
  cursor: pointer;
  transition: background ${tokens.motion.duration} ${tokens.motion.ease};

  &:hover {
    background: ${tokens.surface.glass};
    color: ${tokens.text.secondary};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.accent.base};
    outline-offset: 2px;
  }
`
